<template>
    <div class="form-group mb-3" :class="bsClass">
        <label :for="name" class="form-label" v-if="!labelHidden">
            {{ label }}
            <span class="text-danger" v-if="required"> *</span>
        </label>
        <flat-pickr
            v-model="value"
            :class="errorClassObject()"
            @update:modelValue="updateField(value)"
            :name="name"
            :placeholder="placeholder"
            :config="config"
            :id="name"
            :disabled="disabled"
        ></flat-pickr>
        <span class="invalid-feedback" v-html="errorMessage()"></span>
        <span class="form-text text-muted" v-if="informationContent !== null">
            {{ informationContent }}
        </span>
    </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { French } from 'flatpickr/dist/l10n/fr.js';

export default {
    name: "FormDatePicker",
    components: {
        flatPickr
    },
    props: {
        name: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: true
        },
        labelHidden: {
            type: Boolean,
            required: false,
            default: false
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        informationContent: {
            type: String,
            required: false,
            default: null
        },
        errors: {
            required: true
        },
        placeholder: {
            type: String,
            required: false,
            default: 'jj/mm/aaaa'
        },
        bsClass: {
            type: String,
            required: false,
            default: "col-md-12"
        },
        data: {
            required: false,
            default: null
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        minYear: {
            type: Number,
            required: false,
            default: 1900
        },
        maxYear: {
            type: Number,
            required: false,
            default: 2100
        },
    },
    data() {
        return {
            value: null,
            config: {
                dateFormat: 'Y-m-d',
                altFormat: 'd/m/Y',
                altInput: true,
                allowInput: true,
                disableMobile: true,
                locale: French,
                onReady: (selectedDates, dateStr, instance) => {
                    instance.altInput.addEventListener('input', (e) => {
                        this.formatDateInput(e, instance);
                    });
                    instance.altInput.addEventListener('blur', (e) => {
                        this.validateOnBlur(e, instance);
                    });
                },
            },
        }
    },
    methods: {
        formatDateInput(e, instance) {
            let input = e.target;
            let cursorPosition = input.selectionStart;
            let oldValue = input.value;
            let value = input.value.replace(/\D/g, '');
            let formatted = '';

            // Limite le jour à 31 max
            if (value.length >= 2) {
                let day = parseInt(value.substring(0, 2), 10);
                if (day > 31) day = 31;
                if (day < 1 && value.substring(0, 2) !== '00') day = value.substring(0, 2);
                formatted = day.toString().padStart(2, '0');
            } else if (value.length > 0) {
                formatted = value.substring(0, 2);
            }

            // Limite le mois à 12 max
            if (value.length >= 4) {
                let month = parseInt(value.substring(2, 4), 10);
                if (month > 12) month = 12;
                if (month < 1 && value.substring(2, 4) !== '00') month = value.substring(2, 4);
                formatted += '/' + month.toString().padStart(2, '0');
            } else if (value.length > 2) {
                formatted += '/' + value.substring(2, 4);
            }

            // Année sur 4 chiffres
            if (value.length > 4) {
                formatted += '/' + value.substring(4, 8);
            }

            input.value = formatted;

            // Ajuste la position du curseur
            let newCursorPosition = cursorPosition;
            if (formatted.length > oldValue.length) {
                newCursorPosition = cursorPosition + (formatted.length - oldValue.length);
            }
            input.setSelectionRange(newCursorPosition, newCursorPosition);

            // Met à jour le calendrier si la date est complète et valide
            if (value.length === 8) {
                this.trySetDate(value, instance);
            }
        },

        trySetDate(value, instance) {
            const day = parseInt(value.substring(0, 2), 10);
            const month = parseInt(value.substring(2, 4), 10) - 1;
            const year = parseInt(value.substring(4, 8), 10);

            // Vérifie que l'année est dans la plage autorisée
            if (year < this.minYear || year > this.maxYear) {
                return false;
            }

            const date = new Date(year, month, day);

            // Vérifie que la date est valide (gère les cas comme 31/02)
            if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
                instance.setDate(date, true);
                return true;
            }

            return false;
        },

        validateOnBlur(e, instance) {
            let input = e.target;
            let value = input.value.replace(/\D/g, '');

            // Si le champ est vide, on laisse passer
            if (value.length === 0) {
                return;
            }

            // Si la date n'est pas complète ou invalide, on reset
            if (value.length !== 8) {
                this.resetToLastValidDate(input, instance);
                return;
            }

            const day = parseInt(value.substring(0, 2), 10);
            const month = parseInt(value.substring(2, 4), 10);
            const year = parseInt(value.substring(4, 8), 10);

            // Vérifie les valeurs de base
            if (day < 1 || day > 31 || month < 1 || month > 12) {
                this.resetToLastValidDate(input, instance);
                return;
            }

            // Vérifie l'année
            if (year < this.minYear || year > this.maxYear) {
                this.resetToLastValidDate(input, instance);
                return;
            }

            // Vérifie que la date existe réellement
            const date = new Date(year, month - 1, day);
            if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
                this.resetToLastValidDate(input, instance);
                return;
            }
        },

        resetToLastValidDate(input, instance) {
            if (instance.selectedDates.length > 0) {
                // Remet la dernière date valide
                instance.setDate(instance.selectedDates[0], true);
            } else {
                // Vide le champ
                input.value = '';
                this.value = null;
                this.$emit("update:field", null);
            }
        },

        updateField: function () {
            this.clearErrors(this.name);
            this.$emit("update:field", this.value);
        },
        errorMessage: function () {
            if (this.hasError) {
                return this.errors[this.name][0];
            }
        },
        clearErrors: function () {
            if (this.hasError) {
                this.errors[this.name] = null;
            }
        },
        errorClassObject: function () {
            return this.hasError ? 'form-control flatpickr-input is-invalid' : 'form-control flatpickr-input';
        }
    },
    computed: {
        hasError() {
            return (
                this.errors &&
                this.errors[this.name] &&
                this.errors[this.name].length > 0
            );
        }
    },
    watch: {
        data: {
            immediate: true,
            handler(newVal, oldVal) {
                this.value = newVal;
            },
        }
    }
}
</script>

<style scoped>

</style>
